// Web Push subscription plumbing — reuses the helpers in src/pwa.js and the
// pre-existing /api/push endpoints. System notifications (with the DEVICE's
// own sound) then arrive even when the app/tab is closed.
//
// iPhone note: iOS 16.4+ supports Web Push ONLY for sites added to the Home
// Screen (Share → Add to Home Screen), then Enable notifications inside the
// installed app. Android Chrome works directly in the browser.
import { subscribeToPush } from "../pwa";

const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

export function pushSupport() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
    return "unsupported";
  }
  return Notification.permission; // "default" | "granted" | "denied"
}

async function fetchVapidKey() {
  const res = await fetch("/api/push/vapid-public-key", { headers: authHeaders() });
  const data = await res.json().catch(() => null);
  const key = data?.data?.publicKey || data?.key || "";
  if (!key) throw new Error("Push is not configured on the server");
  return key;
}

async function registerSubscription(sub) {
  const res = await fetch("/api/push/subscribe", {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ subscription: sub.toJSON ? sub.toJSON() : sub, userAgent: navigator.userAgent }),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || data?.status !== 1) throw new Error(data?.message || "Subscribe failed");
}

/**
 * Interactive enable — call from a user click. Requests permission,
 * subscribes this browser, registers it server-side, then asks the backend
 * for a test push so the user immediately sees/hears it.
 * Returns: "enabled" | "denied" | "unsupported" | "error:<msg>"
 */
export async function enablePushNotifications() {
  try {
    const support = pushSupport();
    if (support === "unsupported") return "unsupported";
    const key = await fetchVapidKey();
    const sub = await subscribeToPush(key); // handles requestPermission + subscribe
    if (!sub) return Notification.permission === "denied" ? "denied" : "error:Permission not granted";
    await registerSubscription(sub);
    // Fire the built-in test push so the device proves the pipe end-to-end.
    fetch("/api/push/test", { method: "POST", headers: authHeaders() }).catch(() => {});
    return "enabled";
  } catch (err) {
    const m = err?.message || "Unknown error";
    // "push service error" is a browser↔push-service failure, not a server bug.
    // Give the user the concrete self-fixes instead of a raw DOMException.
    if (/push service|Registration failed|different applicationServerKey/i.test(m)) {
      return "error:Your browser couldn't register with its push service. Try: (1) Brave → enable Settings ▸ Privacy ▸ “Use Google services for push messaging”, then retry. (2) On a corporate network/VPN, fcm.googleapis.com may be blocked — try another network. (3) iPhone → Add the app to your Home Screen first, then enable from the installed app.";
    }
    return `error:${m}`;
  }
}

/**
 * Silent keep-alive — when permission is already granted, refresh the
 * subscription server-side on app load (endpoints rotate occasionally).
 */
export async function ensurePushSubscription() {
  try {
    if (pushSupport() !== "granted") return;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const key = await fetchVapidKey();
      sub = await subscribeToPush(key);
    }
    if (sub) await registerSubscription(sub);
  } catch (_) {
    /* best-effort */
  }
}
